import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { X, Plus, Trash2 } from "lucide-react";
import { useTerm } from "@/lib/terminology.jsx";
import { DEAL_SOURCE_OPTIONS } from "@/lib/dealSourceOptions";

const BASE_STEPS = ["Company", "Round", "Founders", "Traction", "Source"];

function translateLabel(label, term) {
  if (!label) return label;
  return label.split(/(\s+)/).map((tok) => (/^\s+$/.test(tok) ? tok : term(tok))).join("");
}

export default function NewDealModal({ pipelines, stages, pipelineId, onClose, onCreated }) {
  const term = useTerm();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  // v5.8 A4: custom fields spillover
  const [customFields, setCustomFields] = useState([]);
  const [customValues, setCustomValues] = useState({});
  const pipeline = pipelines.find((p) => p.pipeline_id === pipelineId);
  const [form, setForm] = useState({
    pipeline_id: pipelineId,
    fund_id: pipeline?.fund_id || "",
    company_name: "",
    company_description: "",
    company_domain: "",
    company_linkedin_url: "",
    sector: "",
    round_size: "",
    pre_money_valuation: "",
    arr: "",
    burn: "",
    source: "inbound",
    stage_id: stages[0]?.stage_id || "",
    deal_owner_id: "",
    founders: [{ first_name: "", last_name: "", role: "ceo", founder_linkedin_url: "" }],
  });

  useEffect(() => {
    api.get("/team-members").then(({ data }) => setTeamMembers(data));
    // v5.8 A4: fetch attached custom deal fields for this pipeline
    if (pipelineId) {
      api.get("/fields/live", { params: { pipeline_id: pipelineId, module: "deal" } })
        .then(({ data }) => setCustomFields(data.filter((f) => f.is_attachment && f.is_custom && f.field_type !== "computed")))
        .catch(() => {});
    }
  }, [pipelineId]);

  // v5.8 A4: dynamic step spillover if custom fields exist
  const CUSTOM_PAGE_SIZE = 6;
  const customPages = Math.ceil(customFields.length / CUSTOM_PAGE_SIZE);
  const STEPS = customPages > 0
    ? [...BASE_STEPS, ...Array.from({ length: customPages }, (_, i) => `Additional Details${customPages > 1 ? ` (${i + 1}/${customPages})` : ""}`)]
    : BASE_STEPS;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setFounder = (idx, k, v) =>
    setForm((f) => ({
      ...f,
      founders: f.founders.map((x, i) => (i === idx ? { ...x, [k]: v } : x)),
    }));
  const addFounder = () =>
    setForm((f) => ({
      ...f,
      founders: [...f.founders, { first_name: "", last_name: "", role: "cto", founder_linkedin_url: "" }],
    }));
  const removeFounder = (idx) =>
    setForm((f) => ({
      ...f,
      founders: f.founders.filter((_, i) => i !== idx),
    }));

  // Ticket vs Round validation is REMOVED per v5.4 Correction 3 (Proposed Ticket no longer exists in UI).

  const canProceed = () => {
    if (step === 0) return !!form.company_name.trim();
    if (step === 2) return !!form.founders[0]?.first_name?.trim();
    // v5.8 A4: block Next when required custom field on this step is blank
    if (step >= BASE_STEPS.length && customPages > 0) {
      const pageIdx = step - BASE_STEPS.length;
      const pageFields = customFields.slice(pageIdx * CUSTOM_PAGE_SIZE, (pageIdx + 1) * CUSTOM_PAGE_SIZE);
      for (const f of pageFields) {
        if (f.effective_required && !customValues[f.field_definition_id]) return false;
      }
    }
    return true;
  };

  const submit = async () => {
    if (!form.company_name.trim() || !form.founders[0].first_name.trim()) {
      toast.error("Company name and one founder are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      const { data } = await api.post("/deals", payload);
      // v5.8 A4: persist custom field values after deal creation
      const entries = Object.entries(customValues);
      for (const [field_definition_id, value] of entries) {
        if (value === undefined || value === "" || value === null) continue;
        try {
          await api.post(`/fields/values/${data.deal_id}`, {
            field_definition_id,
            module: "deal",
            value,
          });
        } catch { /* non-fatal; deal is already created */ }
      }
      toast.success(`${term("Deal")} created: ${data.company_name}`);
      onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Failed to create ${term("deal").toLowerCase()}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="new-deal-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "var(--cw-border)" }}>
          <div>
            <div className="cw-caption uppercase tracking-widest">Step {step + 1} of {STEPS.length}</div>
            <h2 className="cw-h2 mt-0.5">New {term("Deal")} · {translateLabel(STEPS[step], term)}</h2>
          </div>
          <button onClick={onClose} className="text-[var(--cw-muted)] hover:text-black" data-testid="close-modal-btn">
            <X size={20} />
          </button>
        </div>

        {/* Stepper */}
        <div className="px-6 pt-4 flex gap-1">
          {STEPS.map((s, i) => (
            <div
              key={s}
              className="flex-1 h-1 rounded-full transition"
              style={{ background: i <= step ? "var(--cw-primary)" : "var(--cw-border)" }}
            />
          ))}
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-4">
          {step === 0 && (
            <>
              <Field label="Company Name *">
                <input value={form.company_name} onChange={(e) => set("company_name", e.target.value)}
                  className={inpCls} data-testid="deal-company-name-input" />
              </Field>
              <Field label="Description">
                <textarea value={form.company_description} rows={3} onChange={(e) => set("company_description", e.target.value)}
                  className={inpCls + " py-2"} data-testid="deal-company-description-input" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Domain (used for dedup)">
                  <input value={form.company_domain} placeholder="acme.co" onChange={(e) => set("company_domain", e.target.value)}
                    className={inpCls} data-testid="deal-company-domain-input" />
                </Field>
                <Field label="Sector">
                  <input value={form.sector} onChange={(e) => set("sector", e.target.value)} className={inpCls}
                    data-testid="deal-sector-input" />
                </Field>
              </div>
              {/* v5.8 Addition 21: Company LinkedIn */}
              <Field label="Company LinkedIn (optional)">
                <input value={form.company_linkedin_url}
                  placeholder="https://linkedin.com/company/acme"
                  onChange={(e) => set("company_linkedin_url", e.target.value)}
                  className={inpCls}
                  data-testid="deal-company-linkedin-input" />
              </Field>
              <Field label={`Initial ${term("Stage")}`}>
                <select value={form.stage_id} onChange={(e) => set("stage_id", e.target.value)}
                  className={inpCls} data-testid="deal-stage-select">
                  {stages.map((s) => (
                    <option key={s.stage_id} value={s.stage_id}>{s.stage_name}</option>
                  ))}
                </select>
              </Field>
              <Field label={`${term("Deal")} Owner`}>
                <select value={form.deal_owner_id}
                  onChange={(e) => set("deal_owner_id", e.target.value)}
                  className={inpCls} data-testid="deal-owner-select">
                  <option value="">Unassigned</option>
                  {teamMembers.map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {m.full_name} · {m.job_title || roleLabel(m.role)}
                    </option>
                  ))}
                </select>
              </Field>
            </>
          )}
          {step === 1 && (
            <>
              <div className="text-xs text-[var(--cw-muted)] leading-relaxed">
                Monetary fields accept any text — ranges, narratives, or numbers.
              </div>
              <Field label="Round Size">
                <input value={form.round_size} onChange={(e) => set("round_size", e.target.value)}
                  className={inpCls} placeholder="e.g. 5 Cr" data-testid="deal-round-size-input" />
              </Field>
              <Field label="Pre-money Valuation">
                <input value={form.pre_money_valuation} onChange={(e) => set("pre_money_valuation", e.target.value)}
                  className={inpCls} placeholder="e.g. 40 Cr or 50 Cr" data-testid="deal-pre-money-input" />
              </Field>
            </>
          )}
          {step === 2 && (
            <>
              {form.founders.map((f, i) => (
                <div key={i} className="cw-card p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-semibold text-sm">{term("Founder")} {i + 1}{i === 0 ? " (primary)" : ""}</div>
                    {form.founders.length > 1 && (
                      <button onClick={() => removeFounder(i)} className="text-[var(--cw-error)]" data-testid={`remove-founder-${i}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="First name *">
                      <input value={f.first_name} onChange={(e) => setFounder(i, "first_name", e.target.value)} className={inpCls}
                        data-testid={`founder-${i}-first-name`} />
                    </Field>
                    <Field label="Last name">
                      <input value={f.last_name} onChange={(e) => setFounder(i, "last_name", e.target.value)} className={inpCls}
                        data-testid={`founder-${i}-last-name`} />
                    </Field>
                    <Field label="Role">
                      <select value={f.role} onChange={(e) => setFounder(i, "role", e.target.value)} className={inpCls}
                        data-testid={`founder-${i}-role`}>
                        <option value="ceo">CEO</option>
                        <option value="cto">CTO</option>
                        <option value="cpo">CPO</option>
                        <option value="coo">COO</option>
                        <option value="other">Other</option>
                      </select>
                    </Field>
                  </div>
                  <div className="mt-3">
                    <label className="cw-caption block mb-1">LinkedIn</label>
                    <input value={f.founder_linkedin_url || ""}
                      onChange={(e) => setFounder(i, "founder_linkedin_url", e.target.value)}
                      placeholder="e.g. linkedin.com/in/foundername"
                      className={inpCls}
                      data-testid={`founder-${i}-linkedin`} />
                    <div className="text-[12px] mt-1" style={{ color: "#666666" }}>
                      Paste the {term("founder").toLowerCase()}&apos;s LinkedIn profile URL
                    </div>
                  </div>
                </div>
              ))}
              <button onClick={addFounder} className="cw-btn-ghost inline-flex items-center gap-1 text-sm" data-testid="add-founder-btn">
                <Plus size={14} /> Add {term("Founder")}
              </button>
            </>
          )}
          {step === 3 && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <Field label="ARR / Revenue">
                  <input value={form.arr} onChange={(e) => set("arr", e.target.value)} className={inpCls}
                    placeholder="8L MRR (2x YoY)" data-testid="deal-arr-input" />
                </Field>
                <Field label="Monthly Burn">
                  <input value={form.burn} onChange={(e) => set("burn", e.target.value)} className={inpCls}
                    placeholder="40L/mo" data-testid="deal-burn-input" />
                </Field>
              </div>
            </>
          )}
          {step === 4 && (
            <Field label="Source">
              <select value={form.source} onChange={(e) => set("source", e.target.value)} className={inpCls}
                data-testid="deal-source-select">
                {DEAL_SOURCE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>
          )}
          {/* v5.8 A4: custom fields spillover step(s) */}
          {step >= BASE_STEPS.length && customPages > 0 && (
            <div className="space-y-3" data-testid={`custom-fields-step-${step - BASE_STEPS.length}`}>
              {customFields
                .slice((step - BASE_STEPS.length) * CUSTOM_PAGE_SIZE, (step - BASE_STEPS.length + 1) * CUSTOM_PAGE_SIZE)
                .map((f) => (
                  <Field key={f.field_definition_id}
                    label={`${f.effective_label}${f.effective_required ? " *" : ""}`}>
                    {f.field_type === "multi_line" ? (
                      <textarea rows={3}
                        value={customValues[f.field_definition_id] || ""}
                        onChange={(e) => setCustomValues({ ...customValues, [f.field_definition_id]: e.target.value })}
                        className={inpCls + " py-2"}
                        data-testid={`cf-wizard-${f.field_key}`} />
                    ) : f.field_type === "dropdown" && Array.isArray(f.options) ? (
                      <select value={customValues[f.field_definition_id] || ""}
                        onChange={(e) => setCustomValues({ ...customValues, [f.field_definition_id]: e.target.value })}
                        className={inpCls}
                        data-testid={`cf-wizard-${f.field_key}`}>
                        <option value="">—</option>
                        {f.options.map((o) => <option key={o.option_key} value={o.option_key}>{o.option_label}</option>)}
                      </select>
                    ) : f.field_type === "checkbox" ? (
                      <input type="checkbox"
                        checked={!!customValues[f.field_definition_id]}
                        onChange={(e) => setCustomValues({ ...customValues, [f.field_definition_id]: e.target.checked })}
                        data-testid={`cf-wizard-${f.field_key}`} />
                    ) : (
                      <input
                        type={["number", "decimal", "currency", "percent"].includes(f.field_type) ? "number"
                          : f.field_type === "date" ? "date"
                          : ["url", "email"].includes(f.field_type) ? f.field_type : "text"}
                        value={customValues[f.field_definition_id] || ""}
                        onChange={(e) => setCustomValues({ ...customValues, [f.field_definition_id]: e.target.value })}
                        className={inpCls}
                        data-testid={`cf-wizard-${f.field_key}`} />
                    )}
                  </Field>
                ))}
            </div>
          )}
        </div>

        <div className="border-t px-6 py-4 flex items-center justify-between" style={{ borderColor: "var(--cw-border)" }}>
          <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
            className="cw-btn-ghost disabled:opacity-40" data-testid="prev-step-btn">
            Back
          </button>
          {step < STEPS.length - 1 ? (
            <button onClick={() => setStep((s) => s + 1)} disabled={!canProceed()}
              className="cw-btn-primary" data-testid="next-step-btn">
              Next
            </button>
          ) : (
            <button onClick={submit} disabled={submitting || !form.company_name.trim() || !form.founders[0].first_name.trim()}
              className="cw-btn-primary" data-testid="submit-deal-btn">
              {submitting ? "Creating…" : `Create ${term("Deal")}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const inpCls = "w-full h-10 px-3 rounded-md border bg-white text-sm outline-none";
const ROLE_LABEL = {
  vc_admin: "VC Admin",
  it_admin: "IT Admin",
  senior_vc: "Partner",
  junior_vc: "Analyst",
};
function roleLabel(r) { return ROLE_LABEL[r] || r; }
function Field({ label, children, error }) {
  return (
    <div>
      <label className="cw-caption block mb-1">{label}</label>
      <div style={{ borderColor: error ? "var(--cw-error)" : "var(--cw-border)" }}>
        {children}
      </div>
      {error && <div className="text-xs mt-1" style={{ color: "var(--cw-error)" }}>{error}</div>}
    </div>
  );
}
